import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Terminal, Play, Square, Wrench, AlertTriangle, Minus, Plus, Loader2, CheckCircle2, Languages, Trash2, Settings2, Package, History, Trophy, Flame, BarChart3, Clock, Timer } from 'lucide-react';
import { toast } from 'sonner';
import { STATUS_META } from '../mock/data';
import { PageHeader, Modal, Field } from '../components/common';
import { useAuth } from '../context/AuthContext';
import { useI18n } from '../i18n/I18nContext';
import { useRealtime } from '../realtime/RealtimeContext';
import AwardCelebration from '../components/AwardCelebration';
import api from '../api/client';

const PROBLEM_TYPES = ['Mechanical', 'Electrical', 'Calibration', 'Tooling', 'Material', 'Software', 'Safety', 'Other'];
const SEVERITIES = ['HIGH', 'MEDIUM', 'LOW'];

// Live-ticking elapsed timer that reflects real time since `since` (persisted
// machine.updated_at), so it stays accurate even after navigating away and back.
// Flashes red once elapsed passes targetSecs (the machine's target time).
function ElapsedTimer({ since, label, color, targetSecs, overLabel }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const pad = (x) => String(x).padStart(2, '0');
  const start = since ? new Date(since).getTime() : now;
  const secs = Math.max(0, Math.floor((now - start) / 1000));
  const over = targetSecs > 0 && secs >= targetSecs;
  const hh = Math.floor(secs / 3600);
  const mm = Math.floor((secs % 3600) / 60);
  const ss = secs % 60;
  const fmt = hh > 0 ? `${hh}:${pad(mm)}:${pad(ss)}` : `${mm}:${pad(ss)}`;
  return (
    <div className="flex flex-col items-center gap-1" data-testid="terminal-elapsed-timer">
      <span className="kmt-label" style={{ fontSize: 10 }}>{label}</span>
      <span className={`font-mono-k${over ? ' kmt-flash' : ''}`} data-over={over ? '1' : '0'}
        style={{ fontSize: 32, fontWeight: 700, color: over ? 'var(--kmt-red)' : color, lineHeight: 1 }}
        data-testid="terminal-elapsed-value">{fmt}</span>
      {over && (
        <span className="text-xs font-semibold flex items-center gap-1 kmt-flash" style={{ color: 'var(--kmt-red)' }} data-testid="terminal-timer-over">
          <AlertTriangle size={12} /> {overLabel}
        </span>
      )}
    </div>
  );
}

export default function OperatorTerminal() {
  const { user } = useAuth();
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const langKey = (lang || 'EN').toLowerCase();
  const [machines, setMachines] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [machineId, setMachineId] = useState('');
  const [jobCode, setJobCode] = useState('');
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);

  // problem quick-report
  const [presets, setPresets] = useState({ catalog: [], recent: [] });
  const [problemOpen, setProblemOpen] = useState(false);
  const [settingOpen, setSettingOpen] = useState(false);
  const [settingNote, setSettingNote] = useState('');
  const [settingJob, setSettingJob] = useState('');
  const [settingReason, setSettingReason] = useState('');
  const [settingReasons, setSettingReasons] = useState([]);
  const [myBadges, setMyBadges] = useState([]);
  const [myStreak, setMyStreak] = useState(null);
  const [changeoverTarget, setChangeoverTarget] = useState(0);
  const [shiftSummary, setShiftSummary] = useState(null);
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [celebration, setCelebration] = useState(null);

  const notifyBadges = (r) => {
    setMyBadges(r.badges || []);
    const streak = (r.streaks || []).find((x) => x.current) || null;
    setMyStreak(streak);
    if (!user?.email) return;
    const key = `kmt_badges_seen_${user.email}`;
    let seen = {};
    try { seen = JSON.parse(localStorage.getItem(key) || '{}'); } catch (e) { seen = {}; }
    const months = (r.badges || []).map((b) => b.month);
    if (Array.isArray(seen.months)) {
      const newWin = months.find((m) => !seen.months.includes(m));
      if (newWin) {
        const b = (r.badges || []).find((x) => x.month === newWin);
        toast.success(t('toast.badgeChampion'), { description: t('toast.badgeChampionDesc').replace('{m}', b?.monthLabel || ''), action: { label: t('toast.viewAwards'), onClick: () => navigate('/my-awards') } });
      }
      if (streak && typeof seen.streak === 'number' && streak.length > seen.streak) {
        toast.success(t('toast.streak').replace('{n}', streak.length), { description: t('toast.streakDesc'), action: { label: t('toast.viewAwards'), onClick: () => navigate('/my-awards') } });
      }
    }
    localStorage.setItem(key, JSON.stringify({ months, streak: streak ? streak.length : 0 }));

    // Custom awards given by an admin -> celebration animation on first sighting
    const awards = r.customAwards || [];
    const akey = `kmt_awards_seen_${user.email}`;
    const ids = awards.map((a) => a.id);
    const rawA = localStorage.getItem(akey);
    if (rawA === null) {
      localStorage.setItem(akey, JSON.stringify(ids));
    } else {
      let seenA = [];
      try { seenA = JSON.parse(rawA); } catch (e) { seenA = []; }
      const fresh = awards.find((a) => !seenA.includes(a.id));
      if (fresh) {
        setCelebration(fresh);
        toast.success(t('celebrate.toast'), { description: fresh.title, action: { label: t('toast.viewAwards'), onClick: () => navigate('/my-awards') } });
      }
      localStorage.setItem(akey, JSON.stringify(ids));
    }
  };
  const [pSelectedKey, setPSelectedKey] = useState('');
  const [pReason, setPReason] = useState('');
  const [pType, setPType] = useState('Mechanical');
  const [pSeverity, setPSeverity] = useState('MEDIUM');
  const [submitting, setSubmitting] = useState(false);
  const [manageCat, setManageCat] = useState(false);
  const [catEn, setCatEn] = useState('');
  const [materials, setMaterials] = useState([]);
  const [matCode, setMatCode] = useState('');
  const [matStep, setMatStep] = useState('1');
  const [matNote, setMatNote] = useState('');
  const canRestock = user?.role === 'ADMIN' || user?.role === 'SUPERVISOR';
  const [matUsage, setMatUsage] = useState([]);
  const [showMatHistory, setShowMatHistory] = useState(false);

  const loadJobs = () => api.listJobs().then(setJobs).catch(() => {});
  const loadPresets = () => api.listProblemPresets().then(setPresets).catch(() => {});
  const loadSettingReasons = () => api.listSettingReasons().then(setSettingReasons).catch(() => {});
  const loadMaterials = () => api.listMaterials().then((ms) => {
    setMaterials(ms);
    setMatCode((prev) => prev || ms[0]?.code || '');
  }).catch(() => {});

  const loadMachines = () => Promise.all([
    api.listMachines(),
    api.listAssignments().catch(() => []),
  ]).then(([data, assigns]) => {
    let visible = data;
    if (user?.role === 'OPERATOR') {
      const uname = (user?.name || '').toLowerCase();
      const uemail = (user?.email || '').toLowerCase();
      const mine = new Set(
        (assigns || [])
          .filter((a) => [(a.operator || '').toLowerCase(), (a.operatorEmail || '').toLowerCase()].includes(uname) || (a.operator || '').toLowerCase() === uemail)
          .map((a) => a.machine)
      );
      visible = data.filter((m) => mine.has(m.id) || (m.operator || '').toLowerCase() === uname);
    }
    setMachines(visible);
    return visible;
  });

  const loadShiftSummary = () => api.shiftSummary(user?.name).then((s) => {
    setShiftSummary(s);
    // Auto-open the summary once when the operator's shift has just ended
    if (s?.shiftEnded && s?.shift && user?.email) {
      const key = `kmt_shiftsummary_${user.email}_${(s.window?.start || '').slice(0, 10)}`;
      if (!localStorage.getItem(key)) {
        setSummaryOpen(true);
        localStorage.setItem(key, '1');
      }
    }
  }).catch(() => {});

  useEffect(() => {
    loadMachines().then((visible) => {
      const running = visible.find((m) => m.status === 'RUNNING');
      setMachineId((prev) => prev || running?.id || visible[0]?.id || '');
    }).catch(() => {}).finally(() => setLoading(false));
    loadJobs();
    loadPresets();
    loadMaterials();
    loadSettingReasons();
    api.getChangeoverTarget().then((d) => setChangeoverTarget(d?.target || 0)).catch(() => {});
    if (user?.name) { api.operatorBadges(user.name).then(notifyBadges).catch(() => {}); loadShiftSummary(); }
    // eslint-disable-next-line
  }, []);

  useRealtime(() => {
    loadMachines().catch(() => {});
    loadJobs();
    loadPresets();
    loadMaterials();
    if (user?.name) api.operatorBadges(user.name).then(notifyBadges).catch(() => {});
  });

  const machine = machines.find((m) => m.id === machineId);
  const status = machine?.status || 'IDLE';
  const meta = STATUS_META[status] || STATUS_META.IDLE;
  const tType = (tp) => { const v = t(`ptype.${tp}`); return v === `ptype.${tp}` ? tp : v; };
  const tSev = (s) => { const v = t(`sev.${s}`); return v === `sev.${s}` ? s : v; };
  const tJob = (s) => { const v = t(`jstatus.${s}`); return v === `jstatus.${s}` ? (s || '').replace(/_/g, ' ') : v; };

  const machineJobs = jobs.filter((j) => j.machine === machineId);
  const jobOptions = machineJobs.length ? machineJobs : jobs;
  const inProgressJob = machineJobs.find((j) => j.status === 'IN_PROGRESS');
  const job = jobs.find((j) => j.code === jobCode) || inProgressJob || jobOptions[0];
  const parseQty = (q) => {
    const m = /(\d+)\s*\/\s*(\d+)/.exec(q || '');
    return m ? { done: parseInt(m[1]), total: parseInt(m[2]) } : { done: 0, total: 100 };
  };
  const target = parseQty(job?.qty).total;

  useEffect(() => { setJobCode(''); }, [machineId]);

  useEffect(() => {
    if (!job) return;
    if (jobCode !== job.code) setJobCode(job.code);
    setCount(parseQty(job.qty).done);
    // eslint-disable-next-line
  }, [job?.code]);

  const selectedMat = materials.find((m) => m.code === matCode);
  const loadMatUsage = (code) => api.materialUsage(code).then(setMatUsage).catch(() => setMatUsage([]));
  const adjustStock = async (dir) => {
    if (!selectedMat) return;
    if (dir < 0 && (selectedMat.stock || 0) <= 0) return;
    const step = parseFloat(matStep);
    if (!(step > 0)) { toast.error(t('term.qtyInvalid')); return; }
    const delta = dir * step;
    try {
      const updated = await api.adjustMaterial(matCode, delta, matNote.trim() || undefined, machineId || undefined);
      setMatNote('');
      setMaterials((prev) => prev.map((m) => (m.code === matCode ? updated : m)));
      toast.success(`${updated.name}: ${updated.stock} ${updated.unit || ''}`, { description: dir < 0 ? t('toast.consumed') : t('toast.restocked') });
      if (showMatHistory) loadMatUsage(matCode);
    } catch (e) { toast.error(t('toast.stockFailed'), { description: e?.response?.data?.detail || e.message }); loadMaterials(); }
  };
  useEffect(() => { if (showMatHistory && matCode) loadMatUsage(matCode); /* eslint-disable-next-line */ }, [showMatHistory, matCode]);

  // Auto-select the raw material tied to the current job (set in Production Scheduling).
  useEffect(() => {
    if (job?.material && materials.length) {
      const mm = materials.find((m) => m.name === job.material);
      if (mm) setMatCode(mm.code);
    }
    // eslint-disable-next-line
  }, [job?.code, materials.length]);

  // Combined problem choices (recent + catalog) localized to the current language.
  // Recent entries that duplicate a catalog reason are dropped.
  const choices = useMemo(() => {
    const cat = (presets.catalog || []).map((c) => ({
      key: 'c-' + c.id, label: c[langKey] || c.en, reason: c.en, type: c.type, severity: c.severity,
      translations: { en: c.en, hi: c.hi, pa: c.pa }, kind: 'catalog',
    }));
    const catReasons = new Set(cat.map((c) => (c.reason || '').toLowerCase()));
    const rec = (presets.recent || [])
      .filter((r) => !catReasons.has((r.desc || '').toLowerCase()))
      .map((r, i) => ({
        key: 'r-' + i, label: (r.translations && r.translations[langKey]) || r.desc, reason: r.desc,
        type: r.type, severity: r.severity, translations: r.translations || {}, kind: 'recent',
      }));
    return [...rec, ...cat];
  }, [presets, langKey]);
  const recentChoices = choices.filter((c) => c.kind === 'recent');
  const catalogChoices = choices.filter((c) => c.kind === 'catalog');

  const logProduction = async () => {
    if (!job) { toast.error(t('toast.selectJob')); return; }
    try {
      await api.updateJob(job.code, { qty: `${count}/${target}` });
      toast.success(t('toast.productionLogged'), { description: `${count}/${target} on ${job.code}` });
      loadJobs();
    } catch (e) { toast.error(t('toast.failedLog')); }
  };

  const markComplete = async () => {
    if (!job) { toast.error(t('toast.selectJob')); return; }
    try {
      await api.updateJob(job.code, { qty: `${target}/${target}`, status: 'COMPLETED' });
      setCount(target);
      toast.success(t('toast.jobCompleted'), { description: `${job.code} → ${target}/${target}` });
      loadJobs();
    } catch (e) { toast.error(t('toast.failedComplete')); }
  };

  const doAction = async (action) => {
    if (!machine) return;
    if (action === 'PROBLEM') { openProblem(); return; }
    if (action === 'SETTING') { setSettingNote(''); setSettingReason(''); setSettingJob(job?.code || ''); setSettingOpen(true); return; }
    const nextStatus = { START: 'RUNNING', STOP: 'STOPPED', SETTING: 'SETTING' }[action];
    setMachines((prev) => prev.map((x) => (x.id === machine.id ? { ...x, status: nextStatus } : x)));
    try {
      const updated = await api.machineAction(machine.id, action, user?.name);
      setMachines((prev) => prev.map((x) => (x.id === machine.id ? updated : x)));
      toast.success(`${machine.id} — ${t(`status.${nextStatus.toLowerCase()}`)}`);
    } catch (e) { toast.error(t('toast.actionFailed')); }
  };

  const submitSetting = async () => {
    if (!machine) return;
    const note = settingNote.trim();
    const desc = [settingReason, note].filter(Boolean).join(' — ');
    setMachines((prev) => prev.map((x) => (x.id === machine.id ? { ...x, status: 'SETTING' } : x)));
    try {
      const updated = await api.machineAction(machine.id, 'SETTING', user?.name, { note, reason: settingReason, job: settingJob });
      setMachines((prev) => prev.map((x) => (x.id === machine.id ? updated : x)));
      toast.success(`${machine.id} — ${t('status.setting')}`, desc ? { description: desc } : undefined);
      setSettingOpen(false);
    } catch (e) { toast.error(t('toast.actionFailed')); }
  };

  const openProblem = () => {
    setPSelectedKey(''); setPReason(''); setPType('Mechanical'); setPSeverity('MEDIUM');
    setProblemOpen(true);
  };

  const onSelectChoice = (key) => {
    setPSelectedKey(key);
    const c = choices.find((x) => x.key === key);
    if (c) { setPType(c.type || 'Mechanical'); setPSeverity((c.severity || 'MEDIUM').toUpperCase()); }
  };

  const addCatalog = async () => {
    if (!catEn.trim()) return;
    try { await api.createProblemPreset({ en: catEn.trim(), type: pType, severity: pSeverity }); setCatEn(''); loadPresets(); toast.success(t('toast.addedProblem')); }
    catch (e) { toast.error(t('toast.failedAdd')); }
  };
  const deleteCatalog = async (id) => {
    try { await api.deleteProblemPreset(id); loadPresets(); }
    catch (e) { toast.error(t('toast.deleteFailed')); }
  };

  const submitProblem = async () => {
    if (!machine) return;
    setSubmitting(true);
    let reason; let translations;
    const custom = pReason.trim();
    if (custom) {
      reason = custom;
      try { translations = await api.translateText(custom, langKey); } catch (e) { translations = null; }
    } else {
      const choice = choices.find((c) => c.key === pSelectedKey);
      if (!choice) { toast.error(t('toast.pickProblem')); setSubmitting(false); return; }
      reason = choice.reason; translations = choice.translations;
    }
    setMachines((prev) => prev.map((x) => (x.id === machine.id ? { ...x, status: 'PROBLEM' } : x)));
    try {
      const updated = await api.machineAction(machine.id, 'PROBLEM', user?.name, { reason, type: pType, severity: pSeverity, translations });
      setMachines((prev) => prev.map((x) => (x.id === machine.id ? updated : x)));
      toast.success(`${machine.id} — ${t('toast.problemReported')}`, { description: reason });
      setProblemOpen(false);
      loadPresets();
    } catch (e) { toast.error(t('toast.reportFailed')); }
    finally { setSubmitting(false); }
  };

  if (loading) return <div className="flex items-center justify-center h-screen" style={{ color: 'var(--kmt-muted)' }}><Loader2 className="animate-spin" size={28} /></div>;

  return (
    <div className="kmt-fade-in">
      <PageHeader icon={Terminal} title={t('terminal.title')} subtitle={t('terminal.subtitle')}
        right={<>
          {shiftSummary?.shift && (
            <button className="kmt-btn-ghost flex items-center gap-2" onClick={() => { loadShiftSummary(); setSummaryOpen(true); }} data-testid="shift-summary-btn">
              <BarChart3 size={15} />{t('summary.button')}
            </button>
          )}
          {machines.length === 0 ? (
            <span className="text-sm" style={{ color: 'var(--kmt-amber)' }} data-testid="terminal-no-machine">{t('term.noMachine')}</span>
          ) : (
            <select className="kmt-input w-56" value={machineId} onChange={(e) => setMachineId(e.target.value)} data-testid="terminal-machine-select">
              {machines.map((m) => <option key={m.id} value={m.id}>{m.id} — {m.displayName}</option>)}
            </select>
          )}
        </>} />

      {myBadges.length > 0 && (
        <div className="px-8 pt-6 flex flex-wrap items-center gap-2" data-testid="terminal-my-badges">
          <span className="kmt-label mr-1">{t('term.yourAwards')}</span>
          {myBadges.slice(0, 6).map((b) => (
            <span key={b.month} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs"
              title={`${t('term.fastest')} · ${b.monthLabel} · avg ${b.avgMinutes}m`}
              style={{ background: 'rgba(240,169,43,0.12)', border: '1px solid rgba(240,169,43,0.45)', color: 'var(--kmt-gold)', fontWeight: 600 }}>
              <Trophy size={13} /> {t('term.fastest')} · {b.monthLabel}
            </span>
          ))}
          {myStreak && (
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs" data-testid="terminal-my-streak"
              title={t('term.streakChip').replace('{n}', myStreak.length)}
              style={{ background: 'rgba(240,120,43,0.14)', border: '1px solid rgba(240,120,43,0.5)', color: '#f0783a', fontWeight: 700 }}>
              <Flame size={13} /> {t('term.streakChip').replace('{n}', myStreak.length)}
            </span>
          )}
        </div>
      )}

      <div className="p-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 kmt-panel p-8 flex flex-col items-center justify-center gap-6" style={{ minHeight: 360 }}>
          <div className="text-center">
            <div className="font-mono-k text-lg" style={{ color: 'var(--kmt-gold)' }}>{machine?.id}</div>
            <div className="font-display text-2xl font-semibold mt-1">{machine?.displayName}</div>
            <div className="kmt-label mt-1">{machine?.line} · {machine?.type}</div>
          </div>
          <span className={`kmt-badge ${meta.cls}`} style={{ fontSize: 14, padding: '10px 20px' }}>
            <span className={`kmt-dot ${meta.dot}`} />{t(`status.${status.toLowerCase()}`)}
          </span>
          {status === 'PROBLEM' && (
            <ElapsedTimer since={machine?.updated_at} label={t('term.problemTimer')} color="var(--kmt-red)"
              targetSecs={(machine?.downtimeTargetHours > 0 ? machine.downtimeTargetHours * 3600 : 0)} overLabel={t('term.overTarget')} />
          )}
          {status === 'SETTING' && (
            <ElapsedTimer since={machine?.updated_at} label={t('term.settingTimer')} color="#c084fc"
              targetSecs={((machine?.settingTargetMinutes > 0 ? machine.settingTargetMinutes : changeoverTarget) || 0) * 60} overLabel={t('term.overTarget')} />
          )}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 w-full max-w-2xl mt-2">
            <button className="kmt-panel-2 py-5 rounded-xl flex flex-col items-center gap-2 hover:border-[var(--kmt-green)]" style={{ color: 'var(--kmt-green)' }} onClick={() => doAction('START')} data-testid="terminal-start-btn"><Play size={22} /><span className="text-sm font-medium">{t('common.start')}</span></button>
            <button className="kmt-panel-2 py-5 rounded-xl flex flex-col items-center gap-2 hover:border-[var(--kmt-red)]" style={{ color: 'var(--kmt-red)' }} onClick={() => doAction('STOP')} data-testid="terminal-stop-btn"><Square size={22} /><span className="text-sm font-medium">{t('common.stop')}</span></button>
            <button className="kmt-panel-2 py-5 rounded-xl flex flex-col items-center gap-2 hover:border-[#a855f7]" style={{ color: '#c084fc' }} onClick={() => doAction('SETTING')} data-testid="terminal-setting-btn"><Wrench size={22} /><span className="text-sm font-medium text-center leading-tight">{t('common.materialSetting')}</span></button>
            <button className="kmt-panel-2 py-5 rounded-xl flex flex-col items-center gap-2 hover:border-[var(--kmt-amber)]" style={{ color: 'var(--kmt-amber)' }} onClick={() => doAction('PROBLEM')} data-testid="terminal-problem-btn"><AlertTriangle size={22} /><span className="text-sm font-medium">{t('status.problem')}</span></button>
          </div>
        </div>

        <div className="space-y-6">
          <div className="kmt-panel p-6">
            <div className="kmt-label">{t('terminal.currentJob')}</div>
            <select className="kmt-input mt-2" value={jobCode} onChange={(e) => setJobCode(e.target.value)} data-testid="terminal-job-select">
              {jobOptions.length === 0 && <option value="">{t('term.noJobs')}</option>}
              {jobOptions.map((j) => <option key={j.code} value={j.code}>{j.code} — {j.product}</option>)}
            </select>
            {job && (
              <>
                <div className="font-display font-medium mt-3">{job.product}</div>
                <div className="text-xs mt-0.5 flex items-center gap-2" style={{ color: 'var(--kmt-muted)' }}>
                  {t('term.machineLabel')} {job.machine} · {tJob(job.status)}
                  {inProgressJob && job.code === inProgressJob.code && (
                    <span className="kmt-badge s-progress" style={{ fontSize: 8 }}>{t('term.autoAssigned')}</span>
                  )}
                </div>
                <div className="mt-4">
                  <div className="flex justify-between text-xs mb-1.5" style={{ color: 'var(--kmt-muted)' }}><span>{t('terminal.progress')}</span><span>{count}/{target}</span></div>
                  <div className="h-2 rounded-full" style={{ background: '#0e0e10' }}>
                    <div className="h-2 rounded-full" style={{ width: `${target ? Math.min(100, (count / target) * 100) : 0}%`, background: 'var(--kmt-gold)' }} />
                  </div>
                </div>
              </>
            )}
          </div>
          <div className="kmt-panel p-6">
            <div className="kmt-label">{t('terminal.counter')}</div>
            <div className="flex items-center gap-3 mt-4">
              <button className="kmt-btn-ghost px-4" onClick={() => setCount(Math.max(0, count - 1))} data-testid="terminal-count-minus"><Minus size={18} /></button>
              <input type="number" min="0" value={count}
                onChange={(e) => setCount(Math.max(0, parseInt(e.target.value || '0', 10)))}
                className="kmt-input text-center font-display font-bold"
                style={{ fontSize: 32, color: 'var(--kmt-gold)', padding: '6px 4px' }} data-testid="terminal-count-input" />
              <button className="kmt-btn-ghost px-4" onClick={() => setCount(count + 1)} data-testid="terminal-count-plus"><Plus size={18} /></button>
            </div>
            <button className="kmt-btn-gold w-full mt-5" onClick={logProduction} data-testid="terminal-log-production-btn">{t('terminal.logProduction')}</button>
            <button className="kmt-btn-ghost w-full mt-2 flex items-center justify-center gap-2"
              onClick={markComplete} style={{ color: 'var(--kmt-green)' }} data-testid="terminal-mark-complete-btn">
              <CheckCircle2 size={16} /> {t('terminal.markComplete')}
            </button>
          </div>

          {/* Raw material in use */}
          <div className="kmt-panel p-6" data-testid="terminal-material-card">
            <div className="flex items-center gap-2 kmt-label"><Package size={13} />{t('term.rawMaterial')}</div>
            {job?.material && selectedMat?.name === job.material && (
              <div className="text-xs mt-2 flex items-center gap-1.5" style={{ color: 'var(--kmt-green)' }} data-testid="terminal-material-from-job">
                <CheckCircle2 size={12} /> {t('term.autoSelectedJob')} {job.code}
              </div>
            )}
            <select className="kmt-input mt-3" value={matCode} onChange={(e) => setMatCode(e.target.value)} data-testid="terminal-material-select">
              {materials.length === 0 && <option value="">{t('term.noMaterials')}</option>}
              {materials.map((m) => <option key={m.code} value={m.code}>{m.name} ({m.code})</option>)}
            </select>
            {selectedMat && (
              <>
                <div className="flex items-baseline justify-between mt-4">
                  <span className="text-sm" style={{ color: 'var(--kmt-muted)' }}>{t('term.inStock')}</span>
                  <span className="font-display font-bold" style={{ fontSize: 26, color: selectedMat.stock <= (selectedMat.reorder || 0) ? 'var(--kmt-red)' : 'var(--kmt-gold)' }} data-testid="terminal-material-stock">
                    {selectedMat.stock} <span className="text-sm" style={{ color: 'var(--kmt-muted)' }}>{selectedMat.unit}</span>
                  </span>
                </div>
                <div className="flex items-center gap-2 mt-3">
                  <button className="kmt-btn-ghost flex-1 flex items-center justify-center gap-1" onClick={() => adjustStock(-1)} disabled={(selectedMat.stock || 0) <= 0} data-testid="terminal-material-minus" style={{ color: 'var(--kmt-red)', opacity: (selectedMat.stock || 0) <= 0 ? 0.4 : 1 }}><Minus size={16} />{t('term.use')}</button>
                  <input type="number" min="0" step="any" inputMode="decimal" value={matStep} onChange={(e) => setMatStep(e.target.value)} className="kmt-input text-center" style={{ width: 84 }} data-testid="terminal-material-step" />
                  {canRestock && <button className="kmt-btn-ghost flex-1 flex items-center justify-center gap-1" onClick={() => adjustStock(1)} data-testid="terminal-material-plus" style={{ color: 'var(--kmt-green)' }}><Plus size={16} />{t('term.add')}</button>}
                </div>
                <input className="kmt-input mt-2 text-sm" value={matNote} onChange={(e) => setMatNote(e.target.value)} placeholder={t('term.matNote')} maxLength={120} data-testid="terminal-material-note" />
                <button className="flex items-center gap-1 text-xs mt-3 mx-auto" onClick={() => setShowMatHistory((s) => !s)} data-testid="terminal-material-history-toggle" style={{ color: 'var(--kmt-muted)' }}>
                  <History size={13} />{showMatHistory ? t('term.hideHistory') : t('term.viewHistory')}
                </button>
                {showMatHistory && (
                  <div className="mt-2 rounded-lg overflow-hidden" style={{ border: '1px solid var(--kmt-border)' }} data-testid="terminal-material-history">
                    {matUsage.length === 0 ? (
                      <div className="text-xs text-center py-3" style={{ color: 'var(--kmt-muted-2)' }}>{t('term.noUsage')}</div>
                    ) : (
                      <div className="max-h-52 overflow-y-auto">
                        {matUsage.map((u) => (
                          <div key={u.id} className="flex items-center justify-between px-3 py-2 text-xs" style={{ borderBottom: '1px solid var(--kmt-border)' }} data-testid={`terminal-usage-row-${u.id}`}>
                            <div className="min-w-0">
                              <span className="font-mono-k font-bold" style={{ color: u.delta < 0 ? 'var(--kmt-red)' : 'var(--kmt-green)' }}>{u.delta > 0 ? '+' : ''}{u.delta} {u.unit}</span>
                              <span className="ml-2" style={{ color: 'var(--kmt-muted)' }}>{u.note}{u.machine ? ` · ${u.machine}` : ''}</span>
                            </div>
                            <div className="text-right shrink-0 ml-2">
                              <div style={{ color: 'var(--kmt-text)' }}>{u.actor}</div>
                              <div style={{ color: 'var(--kmt-muted-2)' }}>{new Date(u.ts).toLocaleString()}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* ---- Problem quick-report modal ---- */}
      <Modal open={problemOpen} onClose={() => setProblemOpen(false)} title={`${t('problem.reportTitle')} · ${machine?.id || ''}`}
        footer={<>
          <button className="kmt-btn-ghost" onClick={() => setProblemOpen(false)} data-testid="problem-report-cancel">{t('common.cancel')}</button>
          <button className="kmt-btn-gold flex items-center gap-2" disabled={submitting} onClick={submitProblem} data-testid="problem-report-submit">
            {submitting ? <Loader2 className="animate-spin" size={15} /> : <AlertTriangle size={15} />} {t('problem.reportTitle')}
          </button>
        </>}>
        <div className="space-y-4">
          <Field label={t('problem.choose')}>
            <select className="kmt-input" value={pSelectedKey} onChange={(e) => onSelectChoice(e.target.value)} data-testid="problem-report-choice">
              <option value="">{t('problem.select')}</option>
              {recentChoices.length > 0 && (
                <optgroup label={t('problem.recentGroup')}>
                  {recentChoices.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                </optgroup>
              )}
              <optgroup label={t('problem.commonGroup')}>
                {catalogChoices.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
              </optgroup>
            </select>
            <button type="button" onClick={() => setManageCat((s) => !s)} className="text-xs mt-2 flex items-center gap-1" data-testid="problem-manage-list-toggle" style={{ color: 'var(--kmt-muted)' }}>
              <Settings2 size={12} />{t('term.editList')}
            </button>
            {manageCat && (
              <div className="mt-2 p-3 rounded-lg" style={{ background: 'var(--kmt-panel-2)', border: '1px solid var(--kmt-border)' }} data-testid="problem-catalog-manager">
                <div className="flex gap-2">
                  <input className="kmt-input flex-1" placeholder={t('problem.addLabel')} value={catEn} onChange={(e) => setCatEn(e.target.value)} data-testid="problem-catalog-en" />
                  <button type="button" className="kmt-btn-gold flex items-center gap-1" onClick={addCatalog} data-testid="problem-catalog-add"><Plus size={14} /></button>
                </div>
                <div className="flex flex-wrap gap-2 mt-3">
                  {catalogChoices.map((c) => {
                    const id = c.key.replace('c-', '');
                    return (
                      <span key={c.key} className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs" style={{ background: 'var(--kmt-panel)', border: '1px solid var(--kmt-border)' }}>
                        {c.label}
                        <button type="button" onClick={() => deleteCatalog(id)} data-testid={`problem-catalog-delete-${id}`} style={{ color: 'var(--kmt-muted-2)' }} className="hover:text-[var(--kmt-red)]"><Trash2 size={11} /></button>
                      </span>
                    );
                  })}
                </div>
              </div>
            )}
          </Field>
          <Field label={t('problem.orType')}>
            <div className="relative">
              <Languages size={15} className="absolute left-3 top-3.5" style={{ color: 'var(--kmt-muted-2)' }} />
              <input className="kmt-input" style={{ paddingLeft: 36 }} placeholder={t('problem.describe')} value={pReason}
                onChange={(e) => { setPReason(e.target.value); if (e.target.value) setPSelectedKey(''); }} data-testid="problem-report-custom" />
            </div>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label={t('problem.type')}>
              <select className="kmt-input" value={pType} onChange={(e) => setPType(e.target.value)} data-testid="problem-report-type">
                {PROBLEM_TYPES.map((tp) => <option key={tp} value={tp}>{tType(tp)}</option>)}
              </select>
            </Field>
            <Field label={t('problem.severity')}>
              <select className="kmt-input" value={pSeverity} onChange={(e) => setPSeverity(e.target.value)} data-testid="problem-report-severity">
                {SEVERITIES.map((s) => <option key={s} value={s}>{tSev(s)}</option>)}
              </select>
            </Field>
          </div>
        </div>
      </Modal>

      {/* ---- Material setting note modal ---- */}
      <Modal open={settingOpen} onClose={() => setSettingOpen(false)} title={`${t('common.materialSetting')} · ${machine?.id || ''}`}
        footer={<>
          <button className="kmt-btn-ghost" onClick={() => setSettingOpen(false)} data-testid="setting-cancel">{t('common.cancel')}</button>
          <button className="kmt-btn-gold flex items-center gap-2" onClick={submitSetting} data-testid="setting-submit">
            <Wrench size={15} /> {t('setting.start')}
          </button>
        </>}>
        <div className="space-y-4">
          <div className="text-sm" style={{ color: 'var(--kmt-muted)' }}>{t('setting.hint')}</div>
          <Field label={t('setting.jobLabel')}>
            <select className="kmt-input" value={settingJob} onChange={(e) => setSettingJob(e.target.value)} data-testid="setting-job-select">
              <option value="">{t('setting.noJob')}</option>
              {jobOptions.map((j) => <option key={j.code} value={j.code}>{j.code} · {j.product}</option>)}
            </select>
          </Field>
          {settingReasons.length > 0 && (
            <Field label={t('setting.reasonLabel')}>
              <select className="kmt-input" value={settingReason} onChange={(e) => setSettingReason(e.target.value)} data-testid="setting-reason-select">
                <option value="">{t('setting.reasonPlaceholder')}</option>
                {settingReasons.map((r) => <option key={r.id} value={r.name}>{r.name}</option>)}
              </select>
            </Field>
          )}
          <Field label={t('setting.noteLabel')}>
            <textarea className="kmt-input" rows={3} placeholder={t('setting.notePlaceholder')} value={settingNote}
              onChange={(e) => setSettingNote(e.target.value)} data-testid="setting-note-input" />
          </Field>
        </div>
      </Modal>

      {/* ---- Shift-end summary modal ---- */}
      <Modal open={summaryOpen} onClose={() => setSummaryOpen(false)} title={t('summary.title')}
        footer={<button className="kmt-btn-gold" onClick={() => setSummaryOpen(false)} data-testid="shift-summary-close">{t('summary.close')}</button>}>
        {!shiftSummary?.shift ? (
          <div className="py-6 text-center text-sm" style={{ color: 'var(--kmt-muted)' }} data-testid="shift-summary-noshift">{t('summary.noShift')}</div>
        ) : (
          <div className="space-y-4" data-testid="shift-summary-body">
            <div className="flex items-center gap-2 text-sm">
              <Clock size={15} style={{ color: 'var(--kmt-gold)' }} />
              <span style={{ color: 'var(--kmt-muted)' }}>{shiftSummary.shift}</span>
              {shiftSummary.shiftEnded && <span className="kmt-badge s-completed" style={{ fontSize: 9 }}>{t('summary.shiftEnded')}</span>}
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="kmt-panel-2 p-4 rounded-xl text-center" data-testid="summary-runtime">
                <div className="kmt-label" style={{ fontSize: 9 }}>{t('summary.runtime')}</div>
                <div className="font-display font-bold mt-1" style={{ fontSize: 26, color: 'var(--kmt-green)' }}>{Math.floor(shiftSummary.runtimeMinutes / 60)}h {shiftSummary.runtimeMinutes % 60}m</div>
              </div>
              <div className="kmt-panel-2 p-4 rounded-xl text-center" data-testid="summary-problems">
                <div className="kmt-label" style={{ fontSize: 9 }}>{t('summary.problems')}</div>
                <div className="font-display font-bold mt-1" style={{ fontSize: 26, color: 'var(--kmt-amber)' }}>{shiftSummary.problems.count}</div>
                <div className="text-xs" style={{ color: 'var(--kmt-muted-2)' }}>{shiftSummary.problems.downtimeMinutes}m {t('summary.downtime')}</div>
              </div>
              <div className="kmt-panel-2 p-4 rounded-xl text-center" data-testid="summary-changeovers">
                <div className="kmt-label" style={{ fontSize: 9 }}>{t('summary.changeovers')}</div>
                <div className="font-display font-bold mt-1" style={{ fontSize: 26, color: '#c084fc' }}>{shiftSummary.changeovers.count}</div>
                <div className="text-xs" style={{ color: 'var(--kmt-muted-2)' }}>{shiftSummary.changeovers.minutes}m {t('summary.total')}</div>
              </div>
            </div>
            <div>
              <div className="kmt-label mb-1">{t('summary.machines')}</div>
              {shiftSummary.machines.length === 0 ? (
                <span className="text-sm" style={{ color: 'var(--kmt-muted-2)' }}>—</span>
              ) : (
                <div className="flex flex-wrap gap-1.5">
                  {shiftSummary.machines.map((m) => <span key={m} className="font-mono-k text-xs px-2 py-1 rounded" style={{ background: 'var(--kmt-panel)', border: '1px solid var(--kmt-border)' }}>{m}</span>)}
                </div>
              )}
            </div>
          </div>
        )}
      </Modal>

      <AwardCelebration award={celebration} onClose={() => setCelebration(null)}
        congratsLabel={t('celebrate.congrats')} hintLabel={t('celebrate.hint')} />
    </div>
  );
}
